// UREI 1620 風ビンテージ・カラー プロセッサー (= 改訂版仕様準拠)
// ────────────────────────────────────────────────────────────────────────
// 実機 UREI 1620 (= disco 時代の rotary mixer) のサウンド特徴:
//   1. 入出力 iron-core トランス → 低域 transient で gentle soft saturation
//   2. transformer + op-amp imbalance → L/R 間に微量 crosstalk (= bleed)
//   3. transformer の周波数特性 → わずかな HF rolloff (= "warmth")
//
// 設計改訂点 (= v1 → v2):
//   - **Wet/Dry を連動制御** (dry = 1 - wet) → 合計振幅が 1.0 を超えず headroom 確保
//   - **Curve は固定 tanh(x) baseline**、Drive は **pre-amp GainNode** で制御
//       → Drive 1.0 = ほぼ neutral、Drive 3.0 = 明確な saturation。直感的
//   - **構造単純化**: 入力 stereo に sat 経路を 1 set 通して sum、その後で
//     split + crosstalk → merge。旧 v1 (= channel ごとに sat 経路 2 set) より
//     WaveShaper / フィルタが半減 (= CPU 軽量、コードも明快)
//   - **mono ソース対応**: input GainNode に channelCount=2 +
//     channelInterpretation="speakers" を明示。OBS が mono で配信した時にも
//     ChannelSplitter(2) の ch1 が silent にならない
//   - **setTargetAtTime に setValueAtTime anchor** で Safari のクロスフェード
//     click ノイズ防止
//
// オーディオグラフ (= トポロジー):
//
//   input (stereo 強制)
//     ├→ dryGain (= 0.8 default) ─────────────────────┐
//     │                                                ├→ satSum (stereo)
//     └→ satLp (LP 400Hz)                              │
//          → driveGain (= 2.0 pre-amp)                 │
//          → shaper (tanh 4x oversample)               │
//          → satHf (LP 8kHz, transformer warmth)       │
//          → wetGain (= 0.2 default) ──────────────────┘
//
//   satSum (stereo) → splitter (2ch)
//     ├ ch0 (L direct) ──────────────────────────────┐
//     ├ ch0 → xtalkLp (LP 1.5kHz) → xtalkGainL ──────│─┐  (L→R bleed)
//     │                                              │  │
//     ├ ch1 (R direct) ──────────────────────────────│──│─┐
//     └ ch1 → xtalkLp (LP 1.5kHz) → xtalkGainR ─────┘  │ │  (R→L bleed)
//                                                       │ │
//                                            merger ch0 ┘ │
//                                            merger ch1 ──┘
//                                                          │
//                                                          └→ output

const SAT_CROSSOVER_HZ = 400;
const SAT_HF_ROLLOFF_HZ = 8000;
const XTALK_LP_HZ = 1500;
// 2 バンド crosstalk: 既存の broad band (LP 1.5kHz, gain ~ -45dB) に加えて、
// 低域だけ強めに混ぜる band を 1 つ追加。実機 UREI は電源揺れ起因で
// 低域 = ステレオ分離度が落ちる特性。これで「左右に振り分けた 2 曲のキック /
// ベースが中央で 1 本のグルーヴに溶ける」感じが出る。
const XTALK_LOW_LP_HZ = 400;

// Dynamic Glue (= 入力低域エネルギー → drive 動的増分) のチューニング。
// 30Hz polling、AnalyserNode の RMS から sigmoid map で drive boost を算出。
// 「2 曲のキックが重なった瞬間だけ saturation が深くなる」体感を作る。
const GLUE_POLL_INTERVAL_MS = 33;
// sigmoid threshold (= 低域 RMS がこの値で boost が max/2)。pre-mastered DJ ソースの
// 低域 RMS は典型的に 0.05-0.15。Audio DSP review medium 対応で旧 0.2 → 0.1 に下げて、
// 実音源の dynamic range にカーブを合わせた。
const GLUE_RMS_HALF = 0.1;
const GLUE_MAX_BOOST = 0.8;      // 入力 peak 時の drive 増分 (= base + 0.8 上限)
const GLUE_DRIVE_TIME_CONST = 0.05; // setTargetAtTime の τ。50ms で 63% 到達 = キック粒立ち維持
const GLUE_RAMP_DOWN_TC = 0.1;   // glue OFF 時に base drive へ戻す τ
// Envelope follower の scalar EMA (= peak-hold style)。AnalyserNode の
// smoothingTimeConstant は時間領域 read に効かないので、control-rate (30Hz) の
// jitter を自前で均す必要がある。Audio DSP review medium 対応。
// 上昇は即時 (= キック立ち上がり追従)、下降は ENV_SMOOTH ずつ減衰 (= release)。
const ENV_SMOOTH_DOWN = 0.4;

/** UREI 1620 風 soft saturation curve (= tanh(x) baseline)。
 *  Drive parameter は **pre-amp GainNode** で制御するので、curve はランタイムで
 *  再生成せず init で 1 度だけ作る (= GC 発生なし)。
 *  curve size 4096 = 12-bit 精度、4x oversampling で aliasing は実質ゼロ。 */
function makeUreiCurve(samples = 4096): Float32Array<ArrayBuffer> {
  // TS 5.7+ で Float32Array は generic (= デフォルト ArrayBufferLike)、
  // WaveShaperNode.curve は Float32Array<ArrayBuffer> を要求するので明示。
  const curve = new Float32Array(samples) as Float32Array<ArrayBuffer>;
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1; // -1..+1
    curve[i] = Math.tanh(x);
  }
  return curve;
}

export class Urei1620Block {
  /** 入力 (= Compressor / mediaSource からここに繋ぐ)。mono 入力は stereo に upmix される。 */
  readonly input: GainNode;
  /** 出力 (= 次段 EQ / Destination にここから繋ぐ)。 */
  readonly output: GainNode;

  // ランタイム調整用のノード参照
  private readonly driveGain: GainNode;
  private readonly wetGain: GainNode;
  private readonly dryGain: GainNode;
  private readonly xtalkGainL: GainNode;
  private readonly xtalkGainR: GainNode;
  /** 低域専用 crosstalk (= LP400Hz、broad band より強めに混ぜる) */
  private readonly xtalkGainLowL: GainNode;
  private readonly xtalkGainLowR: GainNode;
  private readonly outputTrim: GainNode;

  // ─── Dynamic Glue (= env-follower based drive modulation) ───────────────
  /** ユーザーが setDrive で指定した「ベース drive 値」。glue ON 時にここに
   *  RMS 由来の boost が乗る。glue OFF 時は driveGain が baseDrive に戻る。 */
  private baseDrive: number;
  /** satLp 出力 (= LP400 の後) から tap して envelope を測る AnalyserNode */
  private readonly envFollowerAnalyser: AnalyserNode;
  private envFollowerBuf: Float32Array<ArrayBuffer> | null = null;
  private envFollowerTimer: ReturnType<typeof setInterval> | null = null;
  /** Dynamic Glue モードの user preference */
  private glueEnabled = false;
  /** peak-hold style EMA で control-rate jitter を均した低域 RMS */
  private lastSmoothRms = 0;

  constructor(ctx: AudioContext, opts: {
    /** WaveShaper への pre-amp。0.5-3.0、default 2.0 (= subtle UREI character)。 */
    drive?: number;
    /** L/R 間 bleed の振幅 (broad band LP1.5k)。0-0.05、default 0.0056 (-45dB)。 */
    crosstalk?: number;
    /** 低域専用 crosstalk (LP400)。broad band より強めに設定して、2 曲のキック /
     *  ベースが中央で溶ける効果を出す。0-0.1、default 0.02 (-34dB)。 */
    lowCrosstalk?: number;
    /** Wet 比率。dry = 1 - wet で連動制御される。0-1、default 0.2。 */
    wetMix?: number;
    /** 出力 makeup gain。Vintage ON 時に Comp 路と知覚音量を揃えるための final trim。
     *  tanh saturation で RMS / peak が落ちる分を吸収する。
     *  0.5-3.0、default 1.41 (= +3dB)。 */
    outputTrim?: number;
  } = {}) {
    const initialDrive = clamp(opts.drive ?? 2.0, 0.5, 3.0);
    const initialCrosstalk = clamp(opts.crosstalk ?? 0.0056, 0, 0.05);
    const initialLowCrosstalk = clamp(opts.lowCrosstalk ?? 0.02, 0, 0.1);
    const initialWet = clamp(opts.wetMix ?? 0.2, 0, 1);
    const initialDry = 1.0 - initialWet;
    const initialTrim = clamp(opts.outputTrim ?? 1.41, 0.5, 3.0);
    this.baseDrive = initialDrive;

    // ─── 入出力 ─────────────────────────────────────
    // input: mono ソース対応のため stereo を強制する。
    // channelCount=2 + channelCountMode="explicit" + channelInterpretation="speakers"
    // の組み合わせで、mono 入力は両 channel に複製される (= "speakers" 規約)。
    // 明示しないと ChannelSplitter(2) の ch1 が silent になる潜在 bug あり。
    this.input = ctx.createGain();
    this.input.channelCount = 2;
    this.input.channelCountMode = "explicit";
    this.input.channelInterpretation = "speakers";

    this.output = ctx.createGain();

    // ─── サチュレーション経路 (stereo) ──────────────────
    // 入力 → LP400 → drive → shaper → LP8k → wetGain
    // LP400: 低域だけサチュらせて中高域は dry のまま (= 「太く」なる)
    // drive: WaveShaper への pre-amp。curve はランタイムで再生成しない
    // shaper: tanh(x) baseline。4x oversample で aliasing 抑制
    // LP8k: transformer の自然な HF rolloff を再現 (= "warmth")
    const satLp = ctx.createBiquadFilter();
    satLp.type = "lowpass";
    satLp.frequency.value = SAT_CROSSOVER_HZ;
    satLp.Q.value = 0.7071;

    this.driveGain = ctx.createGain();
    this.driveGain.gain.value = initialDrive;

    const shaper = ctx.createWaveShaper();
    shaper.curve = makeUreiCurve();
    shaper.oversample = "4x";

    const satHf = ctx.createBiquadFilter();
    satHf.type = "lowpass";
    satHf.frequency.value = SAT_HF_ROLLOFF_HZ;
    satHf.Q.value = 0.7071;

    this.wetGain = ctx.createGain();
    this.wetGain.gain.value = initialWet;

    this.input.connect(satLp);
    satLp.connect(this.driveGain);
    this.driveGain.connect(shaper);
    shaper.connect(satHf);
    satHf.connect(this.wetGain);

    // Envelope follower tap: satLp 出力 (= 低域だけの信号) を AnalyserNode に
    // 分岐。Dynamic Glue ON 時に setInterval で RMS を読んで driveGain を modulate。
    // analyser は audio graph に常時繋がっているが、poll しない限り CPU は使わない。
    // 注意: AnalyserNode.smoothingTimeConstant は **frequency-domain** read 専用で
    // getFloatTimeDomainData には効かない (Web Audio 仕様)。control-rate の jitter は
    // tickEnvFollower 内の scalar EMA (lastSmoothRms) で均す。
    this.envFollowerAnalyser = ctx.createAnalyser();
    this.envFollowerAnalyser.fftSize = 512;
    satLp.connect(this.envFollowerAnalyser);

    // ─── Dry 経路 (stereo) ──────────────────────────
    this.dryGain = ctx.createGain();
    this.dryGain.gain.value = initialDry;
    this.input.connect(this.dryGain);

    // ─── Sum dry + wet → satSum (stereo) ───────────
    const satSum = ctx.createGain();
    this.wetGain.connect(satSum);
    this.dryGain.connect(satSum);

    // ─── クロストーク routing (2 バンド: broad + low) ─────────────────
    // satSum (stereo) を split → 各 channel direct + 他 channel への bleed (2 系統)
    //   broad band: LP 1.5kHz, ~ -45dB (= 実機 transformer の中央値)
    //   low band:   LP 400Hz,  ~ -34dB (= 低域だけ余分に混ぜて 2 曲のキック / ベースが
    //                                     中央で 1 本に溶ける効果。実機は電源揺れ
    //                                     起因で低域ステレオ分離度が落ちる)
    const splitter = ctx.createChannelSplitter(2);
    satSum.connect(splitter);

    // L → R 用 bleed パス (broad band)
    const xtalkLpL = ctx.createBiquadFilter();
    xtalkLpL.type = "lowpass";
    xtalkLpL.frequency.value = XTALK_LP_HZ;
    xtalkLpL.Q.value = 0.7071;
    this.xtalkGainL = ctx.createGain();
    this.xtalkGainL.gain.value = initialCrosstalk;
    splitter.connect(xtalkLpL, 0);
    xtalkLpL.connect(this.xtalkGainL);

    // R → L 用 bleed パス (broad band)
    const xtalkLpR = ctx.createBiquadFilter();
    xtalkLpR.type = "lowpass";
    xtalkLpR.frequency.value = XTALK_LP_HZ;
    xtalkLpR.Q.value = 0.7071;
    this.xtalkGainR = ctx.createGain();
    this.xtalkGainR.gain.value = initialCrosstalk;
    splitter.connect(xtalkLpR, 1);
    xtalkLpR.connect(this.xtalkGainR);

    // L → R 用 bleed パス (low band: LP400Hz でベース / キックだけ混ぜる)
    const xtalkLpLowL = ctx.createBiquadFilter();
    xtalkLpLowL.type = "lowpass";
    xtalkLpLowL.frequency.value = XTALK_LOW_LP_HZ;
    xtalkLpLowL.Q.value = 0.7071;
    this.xtalkGainLowL = ctx.createGain();
    this.xtalkGainLowL.gain.value = initialLowCrosstalk;
    splitter.connect(xtalkLpLowL, 0);
    xtalkLpLowL.connect(this.xtalkGainLowL);

    // R → L 用 bleed パス (low band)
    const xtalkLpLowR = ctx.createBiquadFilter();
    xtalkLpLowR.type = "lowpass";
    xtalkLpLowR.frequency.value = XTALK_LOW_LP_HZ;
    xtalkLpLowR.Q.value = 0.7071;
    this.xtalkGainLowR = ctx.createGain();
    this.xtalkGainLowR.gain.value = initialLowCrosstalk;
    splitter.connect(xtalkLpLowR, 1);
    xtalkLpLowR.connect(this.xtalkGainLowR);

    // Merger: 各 input は mono、複数 source を connect すると自動加算される
    //   merger input 0 (L) = splitter ch0 + xtalkGainR + xtalkGainLowR
    //   merger input 1 (R) = splitter ch1 + xtalkGainL + xtalkGainLowL
    const merger = ctx.createChannelMerger(2);
    splitter.connect(merger, 0, 0); // L direct → L
    splitter.connect(merger, 1, 1); // R direct → R
    this.xtalkGainR.connect(merger, 0, 0);    // R bleed (broad) → L
    this.xtalkGainL.connect(merger, 0, 1);    // L bleed (broad) → R
    this.xtalkGainLowR.connect(merger, 0, 0); // R bleed (low)   → L
    this.xtalkGainLowL.connect(merger, 0, 1); // L bleed (low)   → R

    // makeup gain (= "output trim"): saturation で落ちた knock を取り戻して
    // Vintage ON / OFF の知覚音量を揃える。merger → outputTrim → this.output。
    this.outputTrim = ctx.createGain();
    this.outputTrim.gain.value = initialTrim;
    merger.connect(this.outputTrim);
    this.outputTrim.connect(this.output);
  }

  /** Drive 量 (= WaveShaper pre-amp) を変える。
   *  0.5 = 殆ど neutral / 1.0 = subtle / 2.0 = default sweet spot / 3.0 = aggressive。
   *  Dynamic Glue ON 時は、この base 値に envelope 由来の boost が乗算で加算される。 */
  setDrive(value: number, timeConstant = 0.03): void {
    const clamped = clamp(value, 0.5, 3.0);
    this.baseDrive = clamped;
    // glue OFF なら即時 driveGain に反映、glue ON ならどうせ tickEnvFollower が
    // 上書きするので setTargetAtTime はベース更新の hint として一度走らせる。
    const t = this.input.context.currentTime;
    rampGain(this.driveGain, clamped, t, timeConstant);
  }

  /** クロストーク量 (broad band, LP1.5k)。0.001-0.01 推奨 (-60 ~ -40 dB)。 */
  setCrosstalk(value: number, timeConstant = 0.03): void {
    const clamped = clamp(value, 0, 0.05);
    const t = this.input.context.currentTime;
    rampGain(this.xtalkGainL, clamped, t, timeConstant);
    rampGain(this.xtalkGainR, clamped, t, timeConstant);
  }

  /** 低域クロストーク量 (low band, LP400)。0-0.05 推奨。
   *  上げると 2 曲のキック / ベースが中央でより太く混ざる。 */
  setLowCrosstalk(value: number, timeConstant = 0.03): void {
    const clamped = clamp(value, 0, 0.1);
    const t = this.input.context.currentTime;
    rampGain(this.xtalkGainLowL, clamped, t, timeConstant);
    rampGain(this.xtalkGainLowR, clamped, t, timeConstant);
  }

  /** Dynamic Glue モード ON / OFF。ON で 30Hz poll の envelope follower が起動し、
   *  低域 (LP400 後) の RMS が大きいほど driveGain を baseDrive から up する。
   *  「2 曲のキックが重なった瞬間だけ saturation が深くなる」効果。 */
  setGlueEnabled(on: boolean): void {
    if (on === this.glueEnabled) return;
    this.glueEnabled = on;
    if (on) {
      this.startEnvFollower();
    } else {
      this.stopEnvFollower();
    }
  }

  private startEnvFollower(): void {
    if (this.envFollowerTimer) return;
    if (!this.envFollowerBuf || this.envFollowerBuf.length !== this.envFollowerAnalyser.fftSize) {
      this.envFollowerBuf = new Float32Array(this.envFollowerAnalyser.fftSize) as Float32Array<ArrayBuffer>;
    }
    // 前回 stop 時の残値をリセット (= 古い RMS が boost に持ち越されないように)
    this.lastSmoothRms = 0;
    this.envFollowerTimer = setInterval(() => this.tickEnvFollower(), GLUE_POLL_INTERVAL_MS);
  }

  private stopEnvFollower(): void {
    if (this.envFollowerTimer) {
      clearInterval(this.envFollowerTimer);
      this.envFollowerTimer = null;
    }
    // driveGain を baseDrive まで滑らかに戻す (= 突然 unboost で click ノイズ防止)
    const t = this.input.context.currentTime;
    this.driveGain.gain.setValueAtTime(this.driveGain.gain.value, t);
    this.driveGain.gain.setTargetAtTime(this.baseDrive, t, GLUE_RAMP_DOWN_TC);
  }

  private tickEnvFollower(): void {
    if (!this.envFollowerBuf) return;
    this.envFollowerAnalyser.getFloatTimeDomainData(this.envFollowerBuf);
    let sumSq = 0;
    for (let i = 0; i < this.envFollowerBuf.length; i++) {
      const v = this.envFollowerBuf[i]!;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / this.envFollowerBuf.length);
    // peak-hold style EMA: 上昇は即時 (= キック立ち上がりに即追従)、下降は
    // ENV_SMOOTH_DOWN ずつ減衰 (= release)。control-rate (30Hz) の量子化 jitter を
    // 均し、setTargetAtTime に「滑らかな envelope 値」だけが入るようにする。
    this.lastSmoothRms = Math.max(rms, this.lastSmoothRms * ENV_SMOOTH_DOWN);
    // sigmoid map: rms = 0 → boost 0、rms = GLUE_RMS_HALF で boost = max / 2、
    // 大入力でも GLUE_MAX_BOOST で頭打ち。tanh と同じ「飽和する非線形」を制御
    // レイヤーに乗せる狙い。
    const boost = (GLUE_MAX_BOOST * this.lastSmoothRms) / (this.lastSmoothRms + GLUE_RMS_HALF);
    const target = clamp(this.baseDrive + boost, 0.5, 3.0);
    const t = this.input.context.currentTime;
    this.driveGain.gain.setValueAtTime(this.driveGain.gain.value, t);
    this.driveGain.gain.setTargetAtTime(target, t, GLUE_DRIVE_TIME_CONST);
  }

  /** Wet/Dry 連動制御。dry = 1 - wet で合計振幅 1.0 を維持 (= headroom 確保)。
   *  0 = pure dry (= UREI character なし) / 1 = pure wet (= 純 saturation) / 0.2 default。 */
  setWetMix(value: number, timeConstant = 0.03): void {
    const wet = clamp(value, 0, 1);
    const dry = 1 - wet;
    const t = this.input.context.currentTime;
    rampGain(this.wetGain, wet, t, timeConstant);
    rampGain(this.dryGain, dry, t, timeConstant);
  }

  /** 出力 makeup gain。Vintage ON / OFF の知覚音量を揃えるための最終段 trim。
   *  0.5 (-6 dB) ~ 3.0 (+9.5 dB)、default 1.41 (+3 dB)。 */
  setOutputTrim(value: number, timeConstant = 0.03): void {
    const clamped = clamp(value, 0.5, 3.0);
    const t = this.input.context.currentTime;
    rampGain(this.outputTrim, clamped, t, timeConstant);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** setTargetAtTime の前に setValueAtTime で現在値を anchor する helper。
 *  Safari の WebAudio で「ramp 中に新たな setTargetAtTime を呼ぶ」と
 *  click ノイズが発生する挙動があるため、明示的に anchor して連続化する。 */
function rampGain(node: GainNode, target: number, t: number, tc: number): void {
  node.gain.setValueAtTime(node.gain.value, t);
  node.gain.setTargetAtTime(target, t, tc);
}
